import { blogPosts } from '../data/blogPosts';
import { DEFAULT_META, DEFAULT_OG_IMAGE, SITE_NAME, SITE_ORIGIN } from './siteConfig';

function origin() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return SITE_ORIGIN.replace(/\/$/, '');
}

function buildMeta({ title, description, path, ogType = 'website', robots, jsonLd }) {
  const base = origin();
  const canonicalPath = path.startsWith('/') ? path : `/${path}`;
  const url = `${base}${canonicalPath}`;
  return {
    title,
    description,
    canonicalUrl: url,
    ogType,
    ogImage: DEFAULT_OG_IMAGE,
    robots: robots || DEFAULT_META.robots,
    jsonLd,
  };
}

const STATIC_ROUTES = {
  '/': buildMeta({
    title: DEFAULT_META.title,
    description: DEFAULT_META.description,
    path: '/',
  }),
  '/login': buildMeta({
    title: `Sign In | ${SITE_NAME}`,
    description: 'Sign in to your Stavyn account to access employee retention analytics and flight risk dashboards.',
    path: '/login',
    robots: 'noindex, nofollow',
  }),
  '/signup': buildMeta({
    title: `Accept Invitation | ${SITE_NAME}`,
    description: 'Complete your Stavyn account using your organization invitation link.',
    path: '/signup',
    robots: 'noindex, nofollow',
  }),
  '/about': buildMeta({
    title: `About Stavyn | AI Employee Retention Platform`,
    description:
      'Learn how Stavyn helps HR and people leaders predict turnover, understand flight risk signals, and retain critical talent with AI.',
    path: '/about',
  }),
  '/blog': buildMeta({
    title: `Employee Retention Blog & HR Insights | ${SITE_NAME} Journal`,
    description:
      'Research and practical guides on employee retention, flight risk, predictive HR, market salary data, and reducing voluntary turnover.',
    path: '/blog',
  }),
  '/contact': buildMeta({
    title: `Contact Stavyn | Employee Retention Software`,
    description:
      'Contact the Stavyn team for product questions, partnerships, or support. We help organizations predict and prevent employee turnover.',
    path: '/contact',
  }),
  '/schedule-demo': buildMeta({
    title: `Free Retention Risk Report | ${SITE_NAME}`,
    description:
      'Request a complimentary retention risk report from Stavyn. See flight risk signals and actionable playbooks for your team — no HRIS integration required.',
    path: '/schedule-demo',
  }),
  '/privacy': buildMeta({
    title: `Privacy Policy | ${SITE_NAME}`,
    description: 'Read how Stavyn collects, uses, and protects personal data when you use our employee retention platform.',
    path: '/privacy',
    robots: 'noindex, follow',
  }),
  '/terms': buildMeta({
    title: `Terms of Service | ${SITE_NAME}`,
    description: 'Terms of service for using Stavyn’s AI employee retention and turnover prediction software.',
    path: '/terms',
    robots: 'noindex, follow',
  }),
};

const APP_ROUTE_META = {
  '/app/dashboard': {
    title: `Retention Dashboard | ${SITE_NAME}`,
    description: 'View company-wide employee flight risk, turnover trends, and retention priorities.',
  },
  '/app/employees': {
    title: `Employees | ${SITE_NAME}`,
    description: 'Browse your employee roster with AI-powered turnover risk scores and retention insights.',
  },
  '/app/imports': {
    title: `Data Imports | ${SITE_NAME}`,
    description: 'Import employee and HR data to power Stavyn retention and flight risk models.',
  },
  '/app/risk': {
    title: `Risk Map | ${SITE_NAME}`,
    description: 'Explore team and department flight risk across your organization.',
  },
  '/app/actions': {
    title: `Retention Actions | ${SITE_NAME}`,
    description: 'Track and manage recommended retention actions for at-risk employees.',
  },
  '/app/reports': {
    title: `Reports & Exports | ${SITE_NAME}`,
    description: 'Export retention and risk reports for HR and leadership.',
  },
  '/app/settings': {
    title: `Settings | ${SITE_NAME}`,
    description: 'Manage your Stavyn account, team access, and notification preferences.',
  },
};

function resolveBlogArticleMeta(slug) {
  const post = blogPosts.find((p) => p.slug === slug);
  if (!post) return null;
  const title = post.seoTitle || `${post.title} | ${SITE_NAME} Journal`;
  const description = post.metaDescription || post.excerpt;
  const path = `/blog/${post.slug}`;
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description,
    author: { '@type': 'Organization', name: post.author || SITE_NAME },
    datePublished: post.datePublished || undefined,
    publisher: { '@type': 'Organization', name: SITE_NAME },
    mainEntityOfPage: `${origin()}${path}`,
  };
  return buildMeta({
    title,
    description,
    path,
    ogType: 'article',
    jsonLd,
  });
}

function resolveAppMeta(pathname) {
  if (!pathname.startsWith('/app')) return null;
  const exact = APP_ROUTE_META[pathname];
  if (exact) {
    return buildMeta({
      ...exact,
      path: pathname,
      robots: 'noindex, nofollow',
    });
  }
  if (pathname.startsWith('/app/employees/')) {
    return buildMeta({
      title: `Employee Profile | ${SITE_NAME}`,
      description: 'Employee retention profile with flight risk score, drivers, and recommended actions.',
      path: pathname,
      robots: 'noindex, nofollow',
    });
  }
  return buildMeta({
    title: `${SITE_NAME} App`,
    description: DEFAULT_META.description,
    path: pathname,
    robots: 'noindex, nofollow',
  });
}

/**
 * @param {string} pathname
 */
export function resolvePageMeta(pathname) {
  const path = pathname.split('?')[0].replace(/\/$/, '') || '/';

  if (path.startsWith('/blog/') && path !== '/blog') {
    const slug = path.slice('/blog/'.length);
    const article = resolveBlogArticleMeta(slug);
    if (article) return article;
  }

  const appMeta = resolveAppMeta(path);
  if (appMeta) return appMeta;

  return STATIC_ROUTES[path] || STATIC_ROUTES['/'];
}
